import { useEffect, useState, useCallback } from 'react';
import {
  MessageSquare,
  User,
  Clock,
  AlertCircle,
  CheckCircle2,
  XCircle,
  UserCheck,
  Tag,
  Flag
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuthContext } from '../../contexts/AuthContext';
import omnichannelService, {
  Conversation,
  ConversationFilters
} from '../../services/omnichannelService';
import { formatDistanceToNow } from 'date-fns';
import { useOmnichannelWebSocket } from '../../hooks/useOmnichannelWebSocket';
import { t } from 'i18next';

interface ConversationListProps {
  selectedId?: string;
  onSelect: (conversation: Conversation) => void;
  filters?: ConversationFilters;
}

export default function ConversationList({ selectedId, onSelect, filters }: ConversationListProps) {
  const { theme } = useTheme();
  const { user } = useAuthContext();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const isAdmin = user?.role === 'admin';

  const loadConversations = useCallback(
    async (resetList: boolean = false) => {
      try {
        if (resetList) {
          setLoading(true);
          setPage(1);
        } else {
          setLoadingMore(true);
        }

        const currentPage = resetList ? 1 : page;
        const response = await omnichannelService.getConversations(filters, currentPage, 20);

        // Handle response structure
        if (response && response.data) {
          const newConversations = Array.isArray(response.data) ? response.data : [];

          if (resetList) {
            setConversations(newConversations);
          } else {
            // Append to existing conversations
            setConversations((prev) => [...prev, ...newConversations]);
          }

          // Check if there are more pages
          const totalPages = response.pagination?.total_pages || 1;
          setHasMore(currentPage < totalPages);
        } else {
          console.warn('⚠️ Response missing data field:', response);
          if (resetList) {
            setConversations([]);
          }
          setHasMore(false);
        }
      } catch (error) {
        console.error('❌ Failed to load conversations:', error);
        if (resetList) {
          setConversations([]);
        }
        setHasMore(false);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [filters, page]
  );

  // Set up WebSocket listener for real-time updates
  useOmnichannelWebSocket({
    onNewMessage: () => loadConversations(true),
    onConversationUpdated: () => loadConversations(true)
  });

  // Initial load and when filters change (reset list)
  useEffect(() => {
    loadConversations(true);
  }, [filters]);

  const handleLoadMore = () => {
    setPage((prev) => prev + 1);
    loadConversations(false);
  };

  const handleAssignToMe = async (conversationId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await omnichannelService.assignToMe(conversationId);
      await loadConversations();
    } catch (error) {
      console.error('Failed to assign to me:', error);
    }
  };

  const handleResolve = async (conversationId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await omnichannelService.updateConversationStatus(conversationId, 'resolved');
      await loadConversations();
    } catch (error) {
      console.error('Failed to resolve conversation:', error);
    }
  };

  const handlePriorityChange = async (
    conversationId: string,
    priority: string,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    try {
      await omnichannelService.updateConversationPriority(conversationId, priority);
      await loadConversations();
    } catch (error) {
      console.error('Failed to change priority:', error);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'assigned':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'resolved':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'closed':
        return <XCircle className="h-4 w-4 text-gray-500" />;
      default:
        return null;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'medium':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'low':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  // Helper function to strip HTML tags from text
  const stripHtml = (html: string): string => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  if (loading && conversations.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto" style={{ overflowX: 'hidden' }}>
        {conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
            <MessageSquare className="h-16 w-16 text-gray-400 mb-4" />
            <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
              {t('atrium.noConversations')}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {conversations.map((conversation) => (
              <div
                key={conversation.id}
                className={`p-4 cursor-pointer transition-colors relative group ${
                  selectedId === conversation.id
                    ? theme === 'dark'
                      ? 'bg-blue-900 bg-opacity-30'
                      : 'bg-blue-50'
                    : theme === 'dark'
                      ? 'hover:bg-gray-700'
                      : 'hover:bg-gray-50'
                }`}
              >
                {/* Quick Actions - Show on hover */}
                <div
                  className={`absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity ${
                    theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                  } p-1 rounded shadow-lg z-10`}
                >
                  {/* Assign to Me - Only for admins */}
                  {isAdmin && conversation.status === 'pending' && (
                    <button
                      onClick={(e) => handleAssignToMe(conversation.id, e)}
                      className={`p-1.5 rounded transition-colors ${
                        theme === 'dark'
                          ? 'hover:bg-gray-700 text-gray-300'
                          : 'hover:bg-gray-100 text-gray-600'
                      }`}
                      title="Assign to me"
                    >
                      <UserCheck className="h-4 w-4" />
                    </button>
                  )}

                  {/* Resolve - For admins or assigned agents */}
                  {(isAdmin || conversation.assigned_to === user?.id) &&
                    conversation.status !== 'resolved' &&
                    conversation.status !== 'closed' && (
                      <button
                        onClick={(e) => handleResolve(conversation.id, e)}
                        className={`p-1.5 rounded transition-colors ${
                          theme === 'dark'
                            ? 'hover:bg-gray-700 text-green-400'
                            : 'hover:bg-gray-100 text-green-600'
                        }`}
                        title="Mark as resolved"
                      >
                        <CheckCircle2 className="h-4 w-4" />
                      </button>
                    )}

                  {/* Priority Dropdown - Only for admins */}
                  {isAdmin && (
                    <div className="relative group/priority">
                      <button
                        className={`p-1.5 rounded transition-colors ${
                          theme === 'dark'
                            ? 'hover:bg-gray-700 text-gray-300'
                            : 'hover:bg-gray-100 text-gray-600'
                        }`}
                        title="Change priority"
                      >
                        <Flag className="h-4 w-4" />
                      </button>

                      {/* Priority Options */}
                      <div
                        className={`absolute right-0 mt-1 w-32 rounded shadow-lg border opacity-0 invisible group-hover/priority:opacity-100 group-hover/priority:visible transition-all ${
                          theme === 'dark'
                            ? 'bg-gray-800 border-gray-700'
                            : 'bg-white border-gray-200'
                        }`}
                      >
                        {['urgent', 'high', 'medium', 'low'].map((priority) => (
                          <button
                            key={priority}
                            onClick={(e) => handlePriorityChange(conversation.id, priority, e)}
                            className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                              conversation.priority === priority
                                ? theme === 'dark'
                                  ? 'bg-gray-700 text-white'
                                  : 'bg-gray-100 text-gray-900'
                                : theme === 'dark'
                                  ? 'hover:bg-gray-700 text-gray-300'
                                  : 'hover:bg-gray-50 text-gray-700'
                            }`}
                          >
                            {priority.charAt(0).toUpperCase() + priority.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div onClick={() => onSelect(conversation)}>
                  <div className="flex items-start gap-3">
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      {conversation.customer_avatar ? (
                        <img
                          src={conversation.customer_avatar}
                          alt={conversation.customer_name || 'Customer'}
                          className="h-10 w-10 rounded-full"
                          onError={(e) => {
                            // Fallback to default avatar if image fails to load
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                      ) : null}
                      <div
                        className={`h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center ${conversation.customer_avatar ? 'hidden' : ''}`}
                      >
                        <User className="h-5 w-5 text-white" />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3
                          className={`font-medium truncate ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}
                        >
                          {conversation.customer_name}
                        </h3>
                        <span
                          className={`text-xs ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                          }`}
                        >
                          {formatDistanceToNow(new Date(conversation.updated_at), {
                            addSuffix: true
                          })}
                        </span>
                      </div>

                      {conversation.last_message && (
                        <p
                          className={`text-sm truncate ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                          }`}
                        >
                          {conversation.last_message.sender_type === 'agent' && 'You: '}
                          {stripHtml(conversation.last_message.content)}
                        </p>
                      )}

                      {/* Metadata */}
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        {/* Status */}
                        <div className="flex items-center gap-1">
                          {getStatusIcon(conversation.status)}
                          <span
                            className={`text-xs ${
                              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}
                          >
                            {conversation.status}
                          </span>
                        </div>

                        {/* Priority */}
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${getPriorityColor(
                            conversation.priority
                          )}`}
                        >
                          {conversation.priority}
                        </span>

                        {/* Channel */}
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            theme === 'dark'
                              ? 'bg-gray-700 text-gray-300'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {conversation.channel_type}
                        </span>

                        {/* Unread count */}
                        {conversation.unread_count > 0 && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-red-500 text-white font-medium">
                            {conversation.unread_count}
                          </span>
                        )}

                        {/* Assigned agent */}
                        {conversation.assigned_agent_name && (
                          <span
                            className={`text-xs ${
                              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}
                          >
                            → {conversation.assigned_agent_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Load More Button */}
      {hasMore && conversations.length > 0 && (
        <div
          className={`border-t p-4 flex justify-center ${
            theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
          }`}
        >
          <button
            onClick={handleLoadMore}
            disabled={loadingMore}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              loadingMore
                ? 'opacity-50 cursor-not-allowed'
                : theme === 'dark'
                  ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
          >
            {loadingMore ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                <span>Loading...</span>
              </div>
            ) : (
              'Load More'
            )}
          </button>
        </div>
      )}
    </div>
  );
}
